import { Box, Typography } from "@mui/material";
import ImageComponent from "components/image/ImageComponent";
import LabelValueDisplay from "components/LabelValueDisplay";
import { Quotation, QuoteDetail } from "features/quotation/types";
import { useMemo } from "react";
import { formatDate, formatDateTime, formatWheelRimDiamDisplay } from "utils";

/**
 * CardCarModelPdf component renders a card with car model information.
 * @param {object} props - The component props.
 * @param {QuoteDetail} props.quotation - The quotation object.
 * @param {boolean} props.showTel - Whether to show the customer phone number.
 */
export default function CardCarModelPdf({
  quotation,
  showTel = false,
  showDates = false,
}: {
  quotation: QuoteDetail;
  showTel?: Boolean;
  /** When true, adds 作成日付 / 有効期限 to Row 1 (tire-only PDF layout after QR removal). */
  showDates?: boolean;
}) {
  const hasTireData = useMemo<boolean>(
    () =>
      Boolean(
        quotation.brnd_name &&
          quotation.modl_code &&
          quotation.modl_period_s &&
          quotation.modl_period_e,
      ),
    [quotation],
  );

  const { tireSizeRender, hubRimDisplay } = useMemo(() => {
    if (!quotation) return { tireSizeRender: "", hubRimDisplay: "" };

    let tireSizeStr = "";

    if ("quote_type" in quotation) {
      const quoteDetail = quotation as QuoteDetail;

      if (quoteDetail.quote_type === "tire" && quoteDetail.tbl_quote_detail_tire?.length > 0) {
        const firstTire = quoteDetail.tbl_quote_detail_tire[0];
        const { midt_tire_width, midt_tire_aspect_ratio, midt_tire_rim_diam } = firstTire;
        if (midt_tire_width && midt_tire_aspect_ratio && midt_tire_rim_diam) {
          tireSizeStr = `${midt_tire_width}/${midt_tire_aspect_ratio}R${midt_tire_rim_diam}`;
        }
      } else if (
        quoteDetail.quote_type === "wheel" &&
        quoteDetail.tbl_quote_detail_wheel?.length > 0
      ) {
        const firstWheel = quoteDetail.tbl_quote_detail_wheel[0];
        const firstTire = firstWheel?.tbl_quote_detail_tire?.[0];
        if (firstTire) {
          const { midt_tire_width, midt_tire_aspect_ratio, midt_tire_rim_diam } = firstTire;
          if (midt_tire_width && midt_tire_aspect_ratio && midt_tire_rim_diam) {
            tireSizeStr = `${midt_tire_width}/${midt_tire_aspect_ratio}R${midt_tire_rim_diam}`;
          }
        }
      }
    } else {
      const q = quotation as Quotation;
      const { midt_tire_width, midt_tire_aspect_ratio, midt_tire_rim_diam } = q;
      if (midt_tire_width && midt_tire_aspect_ratio && midt_tire_rim_diam) {
        tireSizeStr = `${midt_tire_width}/${midt_tire_aspect_ratio}R${midt_tire_rim_diam}`;
      }
    }

    /** ハブ径: API `mmmy_mmm_hub1` のみ（タイヤ rim へのフォールバックなし） */
    return {
      tireSizeRender: tireSizeStr,
      hubRimDisplay: formatWheelRimDiamDisplay(quotation.mmmy_mmm_hub1),
    };
  }, [quotation]);

  return (
    <Box sx={{ display: "flex", gap: "34px" }}>
      <Box sx={{ width: "162px" }}>
        <Typography
          sx={{
            fontSize: "31px",
            fontWeight: "medium",
            textAlign: "center",
            color: "var(--gray-700)",
            mb: "40px",
          }}
        >
          お見積書
        </Typography>
        {quotation.car_image_url && quotation.modl_name && (
          <ImageComponent
            sx={{ width: "162px", border: "unset" }}
            src={quotation.car_image_url}
            alt="car"
            objectFit="contain"
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          gap: 0.5,
          minWidth: 600,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "2px",
            flexWrap: "wrap",
          }}
        >
          <LabelValueDisplay
            label="見積番号"
            value={quotation.quote_no}
            containerSx={
              showDates
                ? { width: "calc((100% - 6px) / 4)", minWidth: "170px" }
                : { width: "calc((100% - 2px) / 2)", minWidth: "300px" }
            }
          />
          <LabelValueDisplay
            label="お客様氏名"
            value={quotation.customer_name}
            containerSx={
              showDates
                ? { width: "calc((100% - 6px) / 4)", minWidth: "170px" }
                : { width: "calc((100% - 2px) / 2)", minWidth: "300px" }
            }
          />
          {showDates && (
            <>
              <LabelValueDisplay
                label="作成日付"
                value={formatDateTime(quotation.created_at)}
                containerSx={{ width: "calc((100% - 6px) / 4)", minWidth: "170px" }}
              />
              <LabelValueDisplay
                label="有効期限"
                value={formatDate(quotation.exp_date)}
                containerSx={{ width: "calc((100% - 6px) / 4)", minWidth: "170px" }}
              />
            </>
          )}
          {showTel && (
            <LabelValueDisplay
              label="お客様電話番号"
              value={quotation.customer_tel_no}
              containerSx={{ width: "calc((100% - 2px) / 2)", minWidth: "300px" }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: "2px", flexWrap: "wrap" }}>
          <LabelValueDisplay
            label="選択車両"
            value={
              hasTireData ? (
                <Box sx={{ textAlign: "center" }}>
                  {`${quotation.brnd_name} ${quotation.modl_name}`}
                  <br />
                  {`${quotation.modl_period_s}～${quotation.modl_period_e}`}
                </Box>
              ) : (
                ""
              )
            }
            containerSx={{ width: "calc((100% - 4px) / 3)", minWidth: "220px" }}
          />
          <LabelValueDisplay
            label="タイヤサイズ"
            value={tireSizeRender}
            containerSx={{ width: "calc((100% - 4px) / 3)", minWidth: "220px" }}
          />
          <LabelValueDisplay
            label="ハブ径"
            value={hubRimDisplay}
            containerSx={{ width: "calc((100% - 4px) / 3)", minWidth: "220px" }}
          />
        </Box>
      </Box>
    </Box>
  );
}
